import React, { useEffect, useRef, useState } from 'react';
import { Check, Lock, MoreHorizontal, PenLine, Plus, Tags, Trash2 } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { useAuth } from '../contexts/AuthContext';
import { InfluenceCategory, getInfluenceCategories } from '../services/influencesService';
import {
  TaxonomyDomain,
  TaxonomyItem,
  listTaxonomies,
  createTaxonomy,
  updateTaxonomy,
  deleteTaxonomy,
} from '../services/taxonomiesService';
import { refreshTaxonomyColors } from '../hooks/useTaxonomyColors';
import { asOneOf, useUrlState } from '../hooks/useUrlState';
import { ConfirmModal } from '../components/common/ConfirmModal';
import {
  Button,
  Card,
  CategoryPill,
  EmptyState,
  IconButton,
  Input,
  Menu,
  Page,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Swatch,
  isHexColor,
} from '../components/ui';

type TaxonomyType = 'influence' | 'stakeholder_category' | 'relationship_category' | 'organization_type' | 'issue_category';

interface TaxonomyTypeConfig {
  key: TaxonomyType;
  domain: TaxonomyDomain;
  label: string;
  short: string;
  /** Shorter label so all segments fit on phones */
  mobile: string;
  description: string;
  items: (InfluenceCategory | TaxonomyItem)[];
  setItems: (items: any) => void;
}

const TYPE_ORDER: TaxonomyType[] = ['influence', 'stakeholder_category', 'relationship_category', 'organization_type', 'issue_category'];

const TYPE_CODEC = asOneOf(TYPE_ORDER);

/**
 * Calm category palette: mid-saturation hues that read as dots and as 10% tints.
 * No red — red is the brand accent (primary actions, selection, danger).
 */
const PALETTE: Array<{ hex: string; name: string }> = [
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#8b5cf6', name: 'Violet' },
  { hex: '#d946ef', name: 'Fuchsia' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#84cc16', name: 'Lime' },
  { hex: '#10b981', name: 'Emerald' },
  { hex: '#14b8a6', name: 'Teal' },
  { hex: '#64748b', name: 'Slate' },
];

/** The API clears a colour when it receives null */
const NO_COLOR = null as unknown as string;

const sameColor = (a?: string | null, b?: string | null) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const colorOfItem = (item: InfluenceCategory | TaxonomyItem) => (isHexColor(item.color) ? item.color : undefined);

/** Taxonomy colour dot; a grey outline when no colour is set */
const ColorDot: React.FC<{ color?: string | null }> = ({ color }) =>
  isHexColor(color) ? (
    <Swatch color={color} size="md" className="ring-1 ring-inset ring-black/10" />
  ) : (
    <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-inset ring-neutral-300" aria-hidden="true" />
  );

function ColorPicker({
  selectedColor,
  onColorChange,
  usedColors = [],
}: {
  /** '' means no colour */
  selectedColor: string;
  onColorChange: (color: string) => void;
  usedColors?: string[];
}) {
  const inPalette = !selectedColor || PALETTE.some((c) => sameColor(c.hex, selectedColor));
  const [showCustom, setShowCustom] = useState(!inPalette);
  const [customColor, setCustomColor] = useState(selectedColor || '#64748b');

  const usedCount = (color: string) => usedColors.filter((c) => sameColor(c, color)).length;

  const swatchClass = (selected: boolean) =>
    `relative inline-flex h-8 w-8 items-center justify-center rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 focus-visible:ring-offset-2 sm:h-6 sm:w-6 ${
      selected ? 'ring-2 ring-ink ring-offset-2' : 'ring-1 ring-inset ring-black/10'
    }`;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Colour">
        <button
          type="button"
          role="radio"
          aria-checked={!selectedColor}
          aria-label="No colour"
          title="No colour"
          onClick={() => {
            onColorChange('');
            setShowCustom(false);
          }}
          className={`${swatchClass(!selectedColor)} bg-white ${!selectedColor ? '' : '!ring-neutral-300'}`}
        >
          {/* Diagonal slash */}
          <span className="absolute h-px w-[70%] rotate-45 bg-neutral-300" aria-hidden="true" />
        </button>
        {PALETTE.map(({ hex, name }) => {
          const selected = sameColor(hex, selectedColor);
          const used = usedCount(hex);
          return (
            <button
              key={hex}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={name}
              onClick={() => {
                onColorChange(hex);
                setShowCustom(false);
              }}
              title={used ? `${name} · used by ${used}` : name}
              className={swatchClass(selected)}
              style={{ backgroundColor: hex }}
            >
              {selected ? (
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              ) : used ? (
                <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
              ) : null}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            if (!showCustom && selectedColor) setCustomColor(selectedColor);
            setShowCustom(!showCustom);
          }}
          aria-expanded={showCustom}
          className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-6 sm:px-2.5 ${
            showCustom || !inPalette ? 'bg-neutral-100 text-ink ring-neutral-200' : 'text-neutral-500 ring-neutral-200 hover:text-ink'
          }`}
        >
          {!inPalette && <ColorDot color={selectedColor} />}
          Custom
        </button>
      </div>

      {showCustom && (
        <div className="mt-2.5 flex animate-fade-up items-center gap-2">
          <input
            type="color"
            value={isHexColor(customColor) && customColor.length === 7 ? customColor : '#64748b'}
            onChange={(e) => {
              setCustomColor(e.target.value);
              onColorChange(e.target.value);
            }}
            aria-label="Pick colour"
            className="h-9 w-9 flex-shrink-0 cursor-pointer rounded-xl border-0 bg-transparent p-0"
          />
          <Input
            type="text"
            value={customColor}
            onChange={(e) => {
              setCustomColor(e.target.value);
              onColorChange(e.target.value);
            }}
            aria-label="Hex colour"
            className="w-32 font-ui-mono"
            placeholder="#64748b"
          />
        </div>
      )}
    </div>
  );
}

/** Inline add / edit row */
function TaxonomyForm({
  editingItem,
  onSave,
  onCancel,
  allItems = [],
  defaultColor = '',
}: {
  editingItem: (InfluenceCategory | TaxonomyItem) | null;
  onSave: (data: { label: string; color: string }) => Promise<void>;
  onCancel: () => void;
  allItems?: (InfluenceCategory | TaxonomyItem)[];
  /** Colour preselected for new items ('' = none) */
  defaultColor?: string;
}) {
  const [label, setLabel] = useState(editingItem ? editingItem.label : '');
  const [color, setColor] = useState(editingItem ? colorOfItem(editingItem) || '' : defaultColor);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Colours already used in this domain (excluding the item being edited)
  const usedColors = allItems
    .filter((item) => item.id !== editingItem?.id)
    .map(colorOfItem)
    .filter((c): c is string => !!c);

  useEffect(() => {
    if (editingItem) {
      setLabel(editingItem.label);
      setColor(colorOfItem(editingItem) || '');
    } else {
      setLabel('');
      setColor(defaultColor);
    }
    // defaultColor only seeds a new row
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  // After adding, the next new row starts from the (updated) default again
  const itemCount = allItems.length;
  useEffect(() => {
    if (!editingItem) setColor(defaultColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  const colorInvalid = !!color && !isHexColor(color);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || colorInvalid) return;

    setIsLoading(true);
    setError(null);
    try {
      const hex = color.trim();
      await onSave({ label: label.trim(), color: hex && !hex.startsWith('#') ? `#${hex}` : hex });
      if (!editingItem) {
        setLabel('');
        inputRef.current?.focus();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not save');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
      className="grid grid-cols-1 animate-fade-up gap-x-2 gap-y-3 bg-neutral-50/70 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
    >
      <div className="flex min-w-0 items-center gap-3">
        <ColorDot color={color} />
        <Input
          ref={inputRef}
          autoFocus
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={editingItem ? 'Label' : 'New label'}
          aria-label="Label"
          required
        />
      </div>
      {/* Actions come last on phones, beside the input from sm up */}
      <div className="order-last flex items-center justify-end gap-2 sm:order-none">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="dark" loading={isLoading} disabled={!label.trim() || colorInvalid}>
          {editingItem ? 'Save' : 'Add'}
        </Button>
      </div>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-2 sm:col-span-2 sm:pl-[22px]">
        <ColorPicker
          key={editingItem?.id ?? 'new'}
          selectedColor={color}
          onColorChange={setColor}
          usedColors={usedColors}
        />
        {/* How it reads on other pages */}
        {label.trim() && (
          <span className="hidden min-w-0 pt-0.5 sm:block" title="Preview">
            <CategoryPill label={label.trim()} color={isHexColor(color) ? color : undefined} />
          </span>
        )}
      </div>
      {colorInvalid && <p className="text-[12px] text-primary-700 sm:col-span-2 sm:pl-[22px]">Use a hex colour, e.g. #64748b</p>}
      {error && <p className="text-[12px] text-primary-700 sm:col-span-2 sm:pl-[22px]">{error}</p>}
    </form>
  );
}

function TaxonomyRow({
  item,
  onEdit,
  onDelete,
}: {
  item: InfluenceCategory | TaxonomyItem;
  onEdit: (item: InfluenceCategory | TaxonomyItem) => void;
  onDelete: (item: InfluenceCategory | TaxonomyItem) => void;
}) {
  const color = colorOfItem(item);
  return (
    <li className="group flex items-center gap-3 pr-2 transition-colors first:rounded-t-2xl last:rounded-b-2xl hover:bg-neutral-50">
      <button
        type="button"
        onClick={() => onEdit(item)}
        aria-label={`Edit ${item.label}`}
        className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 py-2 pl-4 text-left focus-visible:bg-neutral-50 focus-visible:outline-none"
      >
        <span title={color ? color.toUpperCase() : 'No colour'} className="inline-flex">
          <ColorDot color={color} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] text-ink">{item.label}</span>
          {item.key && (
            <span className="block truncate font-ui-mono text-[11.5px] text-neutral-400 sm:hidden">{item.key}</span>
          )}
        </span>
        {item.key && (
          <span className="hidden max-w-[40%] truncate font-ui-mono text-[12px] text-neutral-400 sm:block">{item.key}</span>
        )}
        <span className="hidden w-16 text-right font-ui-mono text-[12px] uppercase text-neutral-400 md:block">
          {color || <span className="text-neutral-300">—</span>}
        </span>
        <PenLine
          className="hidden h-3.5 w-3.5 flex-shrink-0 text-neutral-300 opacity-0 transition-opacity group-hover:opacity-100 sm:block"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </button>
      <Menu
        trigger={(props) => (
          <IconButton label={`Actions for ${item.label}`} size="sm" {...props}>
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        )}
        items={[
          { label: 'Edit', icon: PenLine, onSelect: () => onEdit(item) },
          { label: 'Delete', icon: Trash2, danger: true, onSelect: () => onDelete(item) },
        ]}
      />
    </li>
  );
}

export const TaxonomiesPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  const [influences, setInfluences] = useState<InfluenceCategory[]>([]);
  const [stakeholderCats, setStakeholderCats] = useState<TaxonomyItem[]>([]);
  const [relationshipCats, setRelationshipCats] = useState<TaxonomyItem[]>([]);
  const [organizationTypes, setOrganizationTypes] = useState<TaxonomyItem[]>([]);
  const [issueCats, setIssueCats] = useState<TaxonomyItem[]>([]);
  const [selectedType, setSelectedType] = useUrlState<TaxonomyType>('type', 'influence', TYPE_CODEC);
  const [editingItem, setEditingItem] = useState<(InfluenceCategory | TaxonomyItem) | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    item: (InfluenceCategory | TaxonomyItem) | null;
    loading: boolean;
  }>({ open: false, item: null, loading: false });

  const typeConfigs: Record<TaxonomyType, TaxonomyTypeConfig> = {
    influence: {
      key: 'influence',
      domain: 'influence_category',
      label: 'Influence Types',
      short: 'Influence',
      mobile: 'Influence',
      description: 'Used for Influence Graph and influence sliders',
      items: influences,
      setItems: setInfluences,
    },
    stakeholder_category: {
      key: 'stakeholder_category',
      domain: 'stakeholder_category',
      label: 'Stakeholder Categories',
      short: 'Stakeholders',
      mobile: 'People',
      description: 'Used to classify stakeholders',
      items: stakeholderCats,
      setItems: setStakeholderCats,
    },
    relationship_category: {
      key: 'relationship_category',
      domain: 'relationship_category',
      label: 'Relationship Categories',
      short: 'Relationships',
      mobile: 'Relations',
      description: 'Used when creating relationships between entities',
      items: relationshipCats,
      setItems: setRelationshipCats,
    },
    organization_type: {
      key: 'organization_type',
      domain: 'organization_type',
      label: 'Organization Types',
      short: 'Organizations',
      mobile: 'Orgs',
      description: 'Used to classify organizations',
      items: organizationTypes,
      setItems: setOrganizationTypes,
    },
    issue_category: {
      key: 'issue_category',
      domain: 'issue_category',
      label: 'Issue Categories',
      short: 'Issues',
      mobile: 'Issues',
      description: 'Used to classify issues stakeholders take positions on',
      items: issueCats,
      setItems: setIssueCats,
    },
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [influencesData, stakeholderData, relationshipData, organizationData, issueData] = await Promise.all([
          getInfluenceCategories(),
          listTaxonomies('stakeholder_category'),
          listTaxonomies('relationship_category'),
          listTaxonomies('organization_type'),
          listTaxonomies('issue_category'),
        ]);
        setInfluences(influencesData);
        setStakeholderCats(stakeholderData);
        setRelationshipCats(relationshipData);
        setOrganizationTypes(organizationData);
        setIssueCats(issueData);
      } catch (error) {
        console.error('Failed to load taxonomy data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const currentConfig = typeConfigs[selectedType];

  const handleSave = async (data: { label: string; color: string }) => {
    if (editingItem) {
      const updated = await updateTaxonomy(editingItem.id, {
        label: data.label,
        color: data.color || NO_COLOR,
      });
      currentConfig.setItems((prev: any[]) => prev.map((item: any) => (item.id === editingItem.id ? updated : item)));
      setEditingItem(null);
    } else {
      const created = await createTaxonomy({
        domain: currentConfig.domain,
        label: data.label,
        color: data.color || undefined,
      });
      currentConfig.setItems((prev: any[]) => [...prev, created]);
    }
    // Other pages read colours from the shared taxonomy cache
    refreshTaxonomyColors();
  };

  /** Influence areas drive graph colours, so new ones start with the first unused hue; other domains start with none */
  const newItemColor = () => {
    if (selectedType !== 'influence') return '';
    const used = currentConfig.items.map(colorOfItem);
    return (PALETTE.find((c) => !used.some((u) => sameColor(u, c.hex))) ?? PALETTE[0]).hex;
  };

  const handleDelete = (item: InfluenceCategory | TaxonomyItem) => {
    setDeleteConfirm({ open: true, item, loading: false });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.item) return;
    
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    
    try {
      await deleteTaxonomy(deleteConfirm.item.id);
      
      currentConfig.setItems((prev: any[]) => prev.filter((item: any) => item.id !== deleteConfirm.item!.id));
      refreshTaxonomyColors();

      setDeleteConfirm({ open: false, item: null, loading: false });
    } catch (error) {
      console.error('Failed to delete taxonomy item:', error);
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
      // You might want to show an error toast here
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ open: false, item: null, loading: false });
  };

  const handleEdit = (item: InfluenceCategory | TaxonomyItem) => {
    setAdding(false);
    setEditingItem(item);
  };

  const handleStartAdd = () => {
    setEditingItem(null);
    setAdding(true);
  };

  const handleTypeChange = (type: TaxonomyType) => {
    setSelectedType(type);
    setEditingItem(null);
    setAdding(false);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <Page>
          <PageHeader title="Categories" />
          <Card>
            <EmptyState icon={Lock} title="Admins only" />
          </Card>
        </Page>
      </DashboardLayout>
    );
  }

  const items = currentConfig.items;

  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Categories"
          actions={
            <Button size="sm" variant="primary" onClick={handleStartAdd} disabled={isLoading}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              Add
            </Button>
          }
        />

        <SegmentedControl
          label="Category type"
          className="mb-3"
          value={selectedType}
          onChange={handleTypeChange}
          items={TYPE_ORDER.map((type) => ({
            value: type,
            title: `${typeConfigs[type].label} · ${typeConfigs[type].description}`,
            label: (
              <>
                <span className="sm:hidden">{typeConfigs[type].mobile}</span>
                <span className="hidden sm:inline">{typeConfigs[type].short}</span>
                {!isLoading && (
                  <span className="hidden text-[12px] font-normal tabular-nums text-neutral-400 sm:inline">
                    {typeConfigs[type].items.length}
                  </span>
                )}
              </>
            ),
          }))}
        />

        {/* Not the kit Card: its overflow-hidden would clip the row menus */}
        <div className="rounded-2xl border border-neutral-200 bg-white">
          {adding && (
            <div className={`overflow-hidden rounded-t-2xl ${items.length > 0 ? 'border-b border-neutral-200/70' : 'rounded-b-2xl'}`}>
              <TaxonomyForm
                editingItem={null}
                onSave={handleSave}
                onCancel={() => setAdding(false)}
                allItems={items}
                defaultColor={newItemColor()}
              />
            </div>
          )}

          {isLoading ? (
            <div className="divide-y divide-neutral-200/70" aria-busy="true" aria-label="Loading categories">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex h-11 items-center gap-3 px-4">
                  <Skeleton className="h-2.5 w-2.5 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            !adding && (
              <EmptyState
                icon={Tags}
                title="Nothing here yet"
                action={
                  <Button size="sm" variant="secondary" onClick={handleStartAdd}>
                    <Plus className="h-4 w-4" strokeWidth={2} />
                    Add
                  </Button>
                }
              />
            )
          ) : (
            <ul className={`divide-y divide-neutral-200/70 ${adding ? '[&>li:first-child]:rounded-t-none' : ''}`}>
              {items.map((item) =>
                editingItem?.id === item.id ? (
                  <li key={item.id} className="overflow-hidden first:rounded-t-2xl last:rounded-b-2xl">
                    <TaxonomyForm
                      editingItem={editingItem}
                      onSave={handleSave}
                      onCancel={handleCancelEdit}
                      allItems={items}
                    />
                  </li>
                ) : (
                  <TaxonomyRow
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ),
              )}
            </ul>
          )}
        </div>
      </Page>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete category"
        message={
          deleteConfirm.item ? (
            <div className="space-y-2">
              <p>
                Delete <span className="text-ink">“{deleteConfirm.item.label}”</span>? This can’t be undone.
              </p>
              <p className="text-neutral-500">
                Stakeholders and organizations using it are affected, including relationships and influence scores.
              </p>
            </div>
          ) : null
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteConfirm.loading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </DashboardLayout>
  );
};
